/**
 * 알림 설정 — 관리자 드리프트(이상 징후) 이메일 수신 제어. 카드/토글 상세 UI.
 *   - 회사 전체: 기관 단위 드리프트 메일 비활성(모든 ADMIN 영향, confirm 후).
 *   - 개인: 본인 수신 거부 + 사유(PII 사전 안내).
 * 백엔드: GET/PUT /api/v1/admin/me/drift-opt-out + institution drift disabled. (chat-server SSOT)
 */
import { useEffect, useState } from 'react';
import './console-shared.css';
import { Bell, Building, Mail } from '../../icons';
import {
  getDriftOptOut, setDriftOptOut,
  getInstitutionDriftDisabled, setInstitutionDriftDisabled,
  type DriftOptOutStatus,
} from '../../api/adminConsole';
// 채택 3단계 — 스위치·칸·단추를 정본 공통 컴포넌트로(B1·B2·B7·D1).
import { Button, FormField, Input, Switch } from '../../components/shared';
import { t } from '../../lib/console-drift-i18n';
import { formatDateTime } from '../../lib/base-i18n';

const PII_PATTERNS: Array<{ re: RegExp; label: string }> = [
  { re: /\b[A-Za-z0-9._+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}\b/, label: '이메일' },
  { re: /\b\d{6}[-]?\d{7}\b/, label: '주민번호' },
  { re: /\b(?:\+\d{1,3}[\s-]?)?(?:\(?\d{2,4}\)?[\s-]?)?\d{3,4}[\s-]?\d{4}\b/, label: '전화번호' },
];
function detectPiiKinds(text: string): string[] {
  const hits: string[] = [];
  for (const { re, label } of PII_PATTERNS) if (re.test(text)) hits.push(label);
  return hits;
}

export function NotificationSection() {
  const [status, setStatus] = useState<DriftOptOutStatus | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState(false);
  const [instDisabled, setInstDisabled] = useState(false);
  const [instSaving, setInstSaving] = useState(false);

  async function reload() {
    setLoading(true); setError(null);
    try {
      const [s, inst] = await Promise.all([
        getDriftOptOut(),
        getInstitutionDriftDisabled().catch(() => ({ disabled: false })),
      ]);
      setStatus(s); setReason(s.reason ?? ''); setInstDisabled(inst.disabled);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t('drift.fetch_failed'));
    } finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  function flashSaved() { setSavedHint(true); setTimeout(() => setSavedHint(false), 2000); }

  async function toggleInstitutionWide(next: boolean) {
    if (next && !window.confirm(t('drift.confirm.institution_on'))) return;
    setInstSaving(true); setError(null);
    try { const r = await setInstitutionDriftDisabled({ disabled: next }); setInstDisabled(r.disabled); flashSaved(); }
    catch (e: any) { setError(e?.response?.data?.message ?? t('drift.save_failed')); }
    finally { setInstSaving(false); }
  }

  async function applyChange(nextOptedOut: boolean, nextReason: string | null) {
    setSaving(true); setError(null); setSavedHint(false);
    try {
      const r = await setDriftOptOut({ optedOut: nextOptedOut, reason: nextReason });
      setStatus({ optedOut: r.optedOut, reason: r.reason, since: status?.since ?? null, updatedAt: new Date().toISOString() });
      flashSaved();
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'PII_DETECTED') {
        const kinds = (Array.isArray(data.kinds) ? data.kinds : []).map((k: string) => t(`drift.pii.kind.${k}` as Parameters<typeof t>[0])).join(', ');
        setError(`${t('drift.server_pii_block')} (${kinds}). ${t('drift.server_pii_hint')}`);
      } else setError(data?.message ?? t('drift.save_failed'));
    } finally { setSaving(false); }
  }

  const piiKinds = detectPiiKinds(reason);
  const personalOff = status?.optedOut ?? false;
  const effectiveReceiving = !instDisabled && !personalOff;

  return (
    <div className="svc">
      <div className="svc-head">
        <h2>{t('drift.section.heading')}</h2>
        <p>{t('drift.section.intro')}</p>
      </div>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {loading ? (
        <p className="svc-empty">{t('drift.loading')}</p>
      ) : (
        <>
          <div className="svc-notif-hero" data-on={effectiveReceiving ? 'true' : 'false'}>
            <span className="svc-notif-hero__icon"><Mail size={22} /></span>
            <div>
              <div className="svc-notif-hero__label">관리자 드리프트 알림 메일</div>
              <div className="svc-notif-hero__state">
                <span className={`svc-dot ${effectiveReceiving ? 'svc-dot--on' : 'svc-dot--off'}`} />
                {effectiveReceiving ? '수신 중' : (instDisabled ? '회사 전체 비활성' : '내 수신 거부')}
              </div>
            </div>
          </div>

          <section className="svc-cat svc-cat--notif">
            <header className="svc-cat__head">
              <span className="svc-cat__icon"><Building size={17} /></span>
              <span className="svc-cat__title">{t('drift.section.institution_heading')}</span>
              <span className="svc-cat__desc">모든 관리자에게 영향</span>
            </header>
            <div className="svc-card">
              <div className="svc-card__top">
                <span className="svc-card__name">기관 전체 드리프트 메일</span>
                <Switch
                  checked={instDisabled}
                  disabled={instSaving}
                  onCheckedChange={(v) => void toggleInstitutionWide(v)}
                  aria-label={t('drift.toggle.institution')}
                />
              </div>
              <p className="svc-card__detail" style={{ fontFamily: 'inherit' }}>
                {t('drift.section.institution_intro')}
                <strong> {t('drift.section.institution_warning')}</strong>{t('drift.section.institution_disclaimer')}
              </p>
              <div className="svc-card__chips">
                <span className="svc-card__status"><span className={`svc-dot ${instDisabled ? 'svc-dot--off' : 'svc-dot--on'}`} />{instDisabled ? '비활성(전체 발송 안 함)' : '활성(발송)'}</span>
              </div>
            </div>
          </section>

          {status && (
            <section className="svc-cat svc-cat--notif">
              <header className="svc-cat__head">
                <span className="svc-cat__icon"><Bell size={17} /></span>
                <span className="svc-cat__title">{t('drift.section.personal_heading')}</span>
                <span className="svc-cat__desc">본인 계정만 적용</span>
              </header>
              <div className="svc-card">
                <div className="svc-card__top">
                  <span className="svc-card__name">{t('drift.toggle.personal')}</span>
                  <Switch
                    checked={status.optedOut}
                    disabled={saving || instDisabled}
                    onCheckedChange={(v) => void applyChange(v, v ? (reason.trim() || null) : null)}
                    aria-label={t('drift.toggle.personal')}
                  />
                </div>
                {instDisabled && <p className="svc-card__detail" style={{ fontFamily: 'inherit' }}>회사 전체가 비활성 상태라 개인 설정과 무관하게 수신되지 않습니다.</p>}

                {status.optedOut && (
                  <>
                    <div className="svc-field">
                      <FormField
                        label={t('drift.reason.label')}
                        hint={piiKinds.length > 0
                          ? `${t('drift.pii.warning')} (${piiKinds.join(', ')}). ${t('drift.pii.audit_note')}`
                          : '사유는 감사 로그에 기록됩니다(개인정보 입력 자제).'}
                        disabled={saving}
                      >
                        <Input
                          value={reason}
                          maxLength={200}
                          placeholder={t('drift.reason.placeholder')}
                          onChange={(e) => setReason(e.target.value)}
                          fullWidth
                        />
                      </FormField>
                    </div>
                    <div className="svc-card__foot">
                      {status.since && <span className="svc-card__meta">{t('drift.since_label')} {formatDateTime(status.since)}</span>}
                      <Button
                        variant="primary"
                        size="compact"
                        style={{ marginLeft: 'auto' }}
                        disabled={saving}
                        loading={saving}
                        onClick={() => void applyChange(true, reason.trim() || null)}
                      >
                        {saving ? '저장 중…' : t('drift.reason_save_button')}
                      </Button>
                    </div>
                  </>
                )}
                <div className="svc-card__chips">
                  <span className="svc-card__status"><span className={`svc-dot ${personalOff ? 'svc-dot--off' : 'svc-dot--on'}`} />{personalOff ? '수신 거부' : '수신'}</span>
                  {status.updatedAt && <span className="svc-card__chip">갱신 {formatDateTime(status.updatedAt)}</span>}
                </div>
              </div>
            </section>
          )}

          {savedHint && <div className="svc-tmo__notice" role="status">{t('drift.save_hint')}</div>}
        </>
      )}
    </div>
  );
}
